import type { AppTarget } from "./vite-env";

/** Resolve the build-time target. Defaults to "app" so a dev server
 *  without VITE_APP_TARGET set sees the bigger contractor surface
 *  area (rather than a stripped staff console). */
export const APP_TARGET: AppTarget =
  (import.meta.env.VITE_APP_TARGET as AppTarget | undefined) ?? "app";

export const IS_STAFF_CONSOLE = APP_TARGET === "admin";
export const IS_CONTRACTOR_APP = APP_TARGET === "app";

/** Routes that render only on the contractor app build.
 *  Pencilout-staff don't see these in their console; they impersonate
 *  into a specific org from the staff Organizations page if they need
 *  to interact with org-side surfaces. */
export const CONTRACTOR_ONLY_ROUTES = new Set<string>([
  "/",
  "/jobs",
  "/inbox",
  "/clients",
  "/billing",
  "/walkthroughs",
  "/quotes",
  "/pricing-rules",
  // Public quote view (no auth) — lives on the contractor SPA so the
  // homeowner-clickable URL is `app.pencilout.app/q/:token` not the
  // `admin.` subdomain.
  "/q",
]);

/** Routes that render only on the staff console build. */
export const STAFF_ONLY_ROUTES = new Set<string>([
  "/organizations",
  "/users",
  "/pricing-calculator",
  "/settings",
]);

/** Both targets share auth + magic-link consume + legal pages. The
 *  caller's role determines which links they receive in email
 *  (server-side via `web_base_url_for_role`), so a staff user
 *  can't accidentally land on an `app.` consume URL. */
export const SHARED_ROUTES = new Set<string>([
  "/login",
  "/signup",
  "/auth/consume",
  "/terms",
  "/privacy",
]);

export function shouldRenderRoute(path: string): boolean {
  if (SHARED_ROUTES.has(path)) return true;
  if (IS_STAFF_CONSOLE) {
    return STAFF_ONLY_ROUTES.has(path);
  }
  // Contractor app: render contractor-only + walkthrough/quote
  // detail child routes that share the same prefix.
  if (CONTRACTOR_ONLY_ROUTES.has(path)) return true;
  // Allow any route under a contractor prefix (e.g. /jobs/:id).
  for (const prefix of CONTRACTOR_ONLY_ROUTES) {
    if (prefix === "/") continue;
    if (path === prefix || path.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

/** Where "/" should redirect on the staff console — staff don't have
 *  a contractor-style dashboard yet; send them to Organizations as
 *  the natural starting point. */
export const STAFF_ROOT_REDIRECT = "/organizations";

/** Origin of the contractor SPA. The staff console uses this to
 *  redirect impersonation sessions (and any non-staff session that
 *  lands on the staff host) over to the contractor app where the
 *  matching routes actually exist. The bearer cookie is on
 *  ``.pencilout.app`` so the session carries across subdomains.
 *
 *  Override via ``VITE_CONTRACTOR_APP_ORIGIN`` for dev/staging. */
export const CONTRACTOR_APP_ORIGIN: string =
  (import.meta.env.VITE_CONTRACTOR_APP_ORIGIN as string | undefined) ?? "https://app.pencilout.app";

/** Origin of the staff console. Used by the contractor SPA to
 *  bounce a staff session back after Stop-impersonating
 *  (otherwise the staff user lands on the contractor /jobs page
 *  with no org context).
 *
 *  Override via ``VITE_STAFF_CONSOLE_ORIGIN`` for dev/staging. */
export const STAFF_CONSOLE_ORIGIN: string =
  (import.meta.env.VITE_STAFF_CONSOLE_ORIGIN as string | undefined) ??
  "https://admin.pencilout.app";
